import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { X, Check, Phone } from "lucide-react";
import { meta } from "@/analytics/metaPixel";
import PhoneHours, { trackCall } from "./PhoneHours";

export default function ExitIntent({ onCta }) {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [variant] = useState(() => {
    let v = sessionStorage.getItem("ss_exit_variant");
    if (v !== "a" && v !== "b") {
      v = Math.random() < 0.5 ? "a" : "b";
      sessionStorage.setItem("ss_exit_variant", v);
    }
    return v;
  });

  const track = (event) => {
    try { window.posthog?.capture?.(event, { variant }); } catch { /* analytics optional */ }
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event, variant }), keepalive: true,
      });
    } catch { /* tracking optional */ }
  };

  useEffect(() => {
    if (sessionStorage.getItem("ss_exit_shown")) return;
    const open = () => {
      sessionStorage.setItem("ss_exit_shown", "1");
      setShow(true);
      meta.trackCustom ? meta.trackCustom("exit_intent_shown") : window.fbq && window.fbq("trackCustom", "exit_intent_shown");
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) open();
    };
    document.addEventListener("mouseout", onMouseOut);
    const timer = setTimeout(() => {
      if (window.scrollY > 500) open();
    }, 45000);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prefix = variant === "b" ? "exit2" : "exit";
  const bullets = [t(`${prefix}.b1`), t(`${prefix}.b2`), t(`${prefix}.b3`)];

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[80] bg-[#003087]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShow(false)} data-testid="exit-intent-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl border border-[#E2E8F0]"
          >
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-extrabold text-[#003087] leading-snug pr-4" data-testid="exit-title">{t(`${prefix}.title`)}</h3>
              <button data-testid="exit-close" onClick={() => setShow(false)} className="text-[#94A3B8] hover:text-[#003087] p-1 shrink-0" aria-label="chiudi">
                <X size={20} />
              </button>
            </div>
            <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{t(`${prefix}.sub`)}</p>
            <ul className="mt-5 space-y-2.5">
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-[#1A1A1A] font-medium">
                  <Check size={16} className="text-[#00AEEF] shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
            <a data-testid="exit-call-btn" href="tel:+393713142397"
              onClick={() => trackCall("exit_intent_call")}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-[#003087] hover:bg-[#002260] text-white font-bold py-3.5 px-6 rounded-xl transition-colors duration-200">
              <Phone size={18} /> {t("exit.wa")}
            </a>
            <div className="mt-2 flex justify-center"><PhoneHours /></div>
            <div className="mt-2 flex justify-center"><PhoneHours /></div>
            <button data-testid="exit-order-btn" onClick={() => { window.fbq && window.fbq("trackCustom", "exit_intent_order_click"); setShow(false); onCta("visure"); }}
              className="btn-green mt-3 w-full rounded-xl py-3 px-6 text-sm font-bold">
              {t("exit.order")}
            </button>
            <button data-testid="exit-dismiss" onClick={() => setShow(false)}
              className="mt-4 w-full text-center text-xs text-[#94A3B8] underline hover:text-[#64748B] transition-colors duration-200">
              {t("exit.dismiss")}
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
